import type { ScreenContext } from "../../shell/types";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { card, chip, ib, muted, table } from "../../ui/record";
import { field, pills, refuse, select } from "../clients/forms";
import { pageHead } from "../empty";
import { usDay } from "../../../server/rules/week";

// Home Care › Bill rates (brief 1E-16, mockup v66, v67, v68, v74 homecare/rates): each contract's
// bill rate card (client, contract, the version in use, lines, the businesses that give them).
// Pay by title moved to Payroll › Pay rates (pay-rates.ts). A card (/homecare/rates/<id>) opens on
// the version in use today and names the next one with Open (brief 1E-24, mockup v84); its lines
// (each with its own name, service, unit, when it applies and bill rate), "Started from the
// standard prices of …", the Differentials card, the Margin card for people who see financials, and
// Versions, folded to the one in use and the next with Show all. Add line, a line's pencil and Add
// a differential each save into a version from a date: changes dated the same day join one
// version until it starts; the other lines carry over and work already done keeps its version. A
// card holds prices only: a client billed by CMS-1500 has its codes in its CMS-1500 setup.

interface CardRow {
  id: string;
  clientId: string;
  client: string;
  contract: string;
  version: string;
  lines: number;
  givenBy: string[];
}
interface Answer {
  today: string;
  seesMoney: boolean;
  canEditBill: boolean;
  services: { id: string; name: string }[];
  cards: CardRow[];
}

interface NoticeRow {
  under: number;
  percent: number | null;
  amount: number | null;
}
interface Line {
  id: string;
  serviceId: string | null;
  service: string;
  name: string;
  ownName: string | null;
  unitId: string;
  unit: string;
  countedBy: string | null;
  rate: number;
  rateWords: string;
  rule: string | null;
  applies: string;
  appliesHours: number | null;
  appliesPeriodId: string | null;
  appliesWords: string;
  minimumUnits: number | null;
  minimumCharge: number | null;
  minimum: string | null;
  noticeRows: NoticeRow[];
  notice: string | null;
}
interface Diff {
  id: string;
  name: string;
  kind: string;
  whenKind: string;
  periodId: string | null;
  hours: number | null;
  how: string;
  amount: number;
  serviceIds: string[];
  when: string;
  rate: string;
  services: string;
}
interface Version {
  id: string;
  number: number;
  from: string;
  to: string | null;
  fromWords: string;
  toWords: string | null;
  note: string;
  changes: string[];
  status: "in_use" | "next" | "past";
  started: string | null;
  lines: Line[];
  diffs: Diff[];
}
interface BillRateCard {
  id: string;
  today: string;
  client: { id: string; name: string };
  contract: { id: string; label: string };
  usedFor: string;
  claims: boolean;
  canEdit: boolean;
  seesMoney: boolean;
  current: string | null;
  next: { id: string; number: number; from: string; fromWords: string; changes: number } | null;
  versions: Version[];
  margin: { service: string; billed: string; paid: string; margin: string }[] | null;
  choices: {
    services: { id: string; name: string; module: string; unitIds: string[] }[];
    units: {
      id: string;
      name: string;
      label: string;
      countedBy: string;
      rows: NoticeRow[];
      of: "trip" | "visit";
    }[];
    periods: { id: string; name: string }[];
    cardServices: { id: string; name: string }[];
    how: { value: string; label: string }[];
  };
}

export async function billRatesScreen(host: HTMLElement, ctx: ScreenContext) {
  if (ctx.record) return cardScreen(host, ctx, ctx.record);
  const head = pageHead(
    ctx,
    "Each contract's bill rate card. Standard prices are in Settings › Services and prices.",
  );
  const slot = el("div", { class: "ws" });
  host.append(head, slot);
  let a: Answer | null = null;

  async function load() {
    const res = await api<Answer>("GET", "/api/rates");
    if (!res.ok) {
      slot.replaceChildren(
        el("p", { class: "note", role: "alert" }, "Bill rates couldn't be loaded."),
      );
      return;
    }
    a = res.body;
    draw(a);
  }

  function draw(x: Answer) {
    slot.replaceChildren(
      x.cards.length
        ? table(
            [
              { h: "Client", f: (r: CardRow) => el("b", {}, r.client) },
              { h: "Contract", f: (r) => r.contract },
              { h: "Version", f: (r) => r.version },
              { h: "Lines", num: true, f: (r) => String(r.lines) },
              { h: "Given by", f: (r) => el("span", {}, ...r.givenBy.map((n) => chip(n))) },
            ],
            x.cards,
            (r) => ctx.go(`/homecare/rates/${r.id}`),
            "Bill rate cards",
          )
        : el(
            "div",
            { class: "blank" },
            icon("tag", "big"),
            el("h2", {}, "No bill rate cards yet"),
            el(
              "p",
              { class: "muted" },
              "A client's contract gets its bill rate card from Bill rate card on the client's record.",
            ),
          ),
    );
  }

  await load();
}

const WHEN_OTHER: [string, string][] = [
  ["week_hours", "Past a number of hours a week"],
  ["day_hours", "Past a number of hours a day"],
  ["short_notice", "Booked with short notice"],
  ["full_day", "A full 24 hours"],
];

/** One contract's bill rate card (/homecare/rates/<id>). */
async function cardScreen(host: HTMLElement, ctx: ScreenContext, id: string) {
  let picked: string | null = null;
  let allVersions = false;
  const slot = el("div", { class: "ws" });
  host.append(slot);

  async function load() {
    const res = await api<BillRateCard>("GET", `/api/bill-rate-cards/${encodeURIComponent(id)}`);
    if (!res.ok) {
      slot.replaceChildren(
        el("p", { class: "note", role: "alert" }, "This bill rate card couldn't be loaded."),
      );
      return;
    }
    ctx.crumb(res.body.client.name);
    draw(res.body);
  }

  function draw(c: BillRateCard) {
    const v = c.versions.find((x) => x.id === (picked ?? c.current)) ?? c.versions[0];
    const inUse = c.versions.find((x) => x.id === c.current);
    const editable = c.canEdit;
    const open = el(
      "button",
      { type: "button", class: "btn" },
      icon("organization"),
      " Open client",
    );
    open.addEventListener("click", () => ctx.go(`/homecare/clients/${c.client.id}`));
    const addLine = el("button", { type: "button", class: "btn pri" }, icon("plus"), " Add line");
    addLine.addEventListener("click", () => lineModal(c));
    const head = el(
      "div",
      { class: "head" },
      el(
        "div",
        { class: "t" },
        el("div", { class: "eyebrow" }, "Bill rates"),
        el("h1", {}, icon("tag", "h1i"), `Bill rate card · ${c.client.name}`),
        el("div", { class: "muted" }, `${c.contract.label} · ${c.usedFor}`),
      ),
      el("div", { class: "actions" }, open, ...(editable ? [addLine] : [])),
    );
    if (!v) {
      slot.replaceChildren(head, muted("No versions yet."));
      return;
    }
    const go = (to: string | null) => {
      picked = to;
      draw(c);
    };
    const status = (x: Version) =>
      x.status === "in_use"
        ? chip("In use", "ok")
        : x.status === "next"
          ? chip(`Starts ${x.fromWords}`, "info")
          : chip("Past");
    // Brief 1E-24: the version in use names the next one; the next one goes back.
    const back =
      inUse && v.id !== inUse.id
        ? (() => {
            const b = el(
              "button",
              { type: "button", class: "btn sm" },
              `Back to version ${inUse.number}`,
            );
            b.addEventListener("click", () => go(null));
            return b;
          })()
        : null;
    const banner =
      c.next && v.id !== c.next.id
        ? (() => {
            const n = c.next;
            const b = el("button", { type: "button", class: "btn sm" }, `Open version ${n.number}`);
            b.addEventListener("click", () => go(n.id));
            return el(
              "div",
              {
                class: "authrow",
                style:
                  "display:flex;justify-content:space-between;gap:8px;align-items:center;flex-wrap:wrap;margin:0 0 10px",
              },
              el(
                "span",
                {},
                icon("calendar"),
                " ",
                el("b", {}, `Version ${n.number} starts ${n.fromWords}`),
                el(
                  "span",
                  { class: "small muted" },
                  ` · ${n.changes} change${n.changes === 1 ? "" : "s"}`,
                ),
              ),
              b,
            );
          })()
        : null;
    const versionCard = el(
      "section",
      { class: "card" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, `Version ${v.number} `, status(v)),
          el(
            "span",
            { class: "small muted" },
            `From ${v.fromWords}${v.toWords ? ` to ${v.toWords}` : ""}`,
          ),
          v.started ? el("div", { class: "small muted" }, v.started) : null,
        ),
        back,
      ),
      banner,
      v.lines.length
        ? table(
            [
              {
                h: "Line",
                f: (l: Line) =>
                  el(
                    "span",
                    {},
                    el("b", {}, l.name),
                    ...[l.notice, l.minimum, l.rule]
                      .filter((t): t is string => !!t)
                      .map((t) => el("div", { class: "small muted" }, t)),
                  ),
              },
              { h: "Service", f: (l) => l.service },
              { h: "Unit", f: (l) => l.unit },
              { h: "Applies", f: (l) => l.appliesWords },
              { h: "Bill rate", num: true, f: (l) => el("b", {}, l.rateWords) },
              {
                h: "",
                num: true,
                f: (l) =>
                  editable && v.status !== "past"
                    ? ib("pencil", `Change ${l.name}`, () => lineModal(c, l))
                    : "",
              },
            ],
            v.lines,
            undefined,
            "Lines",
          )
        : muted("No lines on this version."),
    );
    const claimsNote = c.claims
      ? (() => {
          const b = el(
            "button",
            { type: "button", class: "btn sm ghost", style: "padding:0 4px;min-height:0" },
            "CMS-1500 setup",
          );
          b.addEventListener("click", () => ctx.go(`/homecare/clients/${c.client.id}`));
          return el(
            "div",
            { class: "note", style: "margin:0 0 4px" },
            el(
              "p",
              {},
              "This client is billed by invoice and CMS-1500. Codes and the rendering provider are in its ",
              b,
              ".",
            ),
          );
        })()
      : null;
    const diffs = card(
      el("span", {}, icon("clock"), " Differentials"),
      "When a different bill rate applies. The highest one that applies is used.",
      editable && v.status !== "past" ? [ib("plus", "Add a differential", () => diffModal(c))] : [],
      v.diffs.length
        ? table(
            [
              { h: "Name", f: (d: Diff) => el("b", {}, d.name) },
              { h: "When", f: (d) => d.when },
              { h: "Bill rate", f: (d) => el("b", {}, d.rate) },
              { h: "Services", f: (d) => d.services },
              {
                h: "",
                num: true,
                f: (d) =>
                  editable && v.status !== "past"
                    ? ib("pencil", `Change ${d.name}`, () => diffModal(c, d))
                    : "",
              },
            ],
            v.diffs,
            undefined,
            "Differentials",
          )
        : el(
            "p",
            { class: "muted", style: "margin:0" },
            "None. The lines' bill rates apply at all times.",
          ),
    );
    const margin =
      c.seesMoney && c.margin
        ? card(
            el("span", {}, icon("graph"), " Margin"),
            "Seen only by people who see financials. Pay comes from pay by title, pay packages and the driver's pay.",
            [],
            c.margin.length
              ? table(
                  [
                    {
                      h: "Service",
                      f: (r: NonNullable<BillRateCard["margin"]>[number]) => el("b", {}, r.service),
                    },
                    { h: "Bill rate", num: true, f: (r) => r.billed },
                    { h: "Pay rate", num: true, f: (r) => r.paid },
                    { h: "Margin", num: true, f: (r) => el("b", {}, r.margin) },
                  ],
                  c.margin,
                  undefined,
                  "Margin",
                )
              : muted("No pay by title set for these services."),
          )
        : null;
    // Brief 1E-24: folded to the version in use and the next one, with Show all.
    const folded = c.versions.filter((x) => x.status !== "past" || x.id === c.current);
    const rows = allVersions || folded.length === c.versions.length ? c.versions : folded;
    const more =
      c.versions.length > folded.length
        ? (() => {
            const b = el(
              "button",
              { type: "button", class: "btn sm ghost" },
              allVersions ? "Show fewer" : `Show all ${c.versions.length}`,
            );
            b.addEventListener("click", () => {
              allVersions = !allVersions;
              draw(c);
            });
            return [b];
          })()
        : [];
    const versions = card(
      el("span", {}, icon("history"), " Versions"),
      null,
      more,
      table(
        [
          { h: "Version", f: (x: Version) => el("b", {}, String(x.number)) },
          { h: "From", f: (x) => x.fromWords },
          { h: "To", f: (x) => x.toWords ?? "–" },
          { h: "Status", f: (x) => status(x) },
          { h: "What changed", f: (x) => el("span", { class: "small" }, x.note) },
        ],
        rows,
        undefined,
        "Versions",
      ),
    );
    slot.replaceChildren(
      head,
      versionCard,
      ...(claimsNote ? [claimsNote] : []),
      diffs,
      ...(margin ? [margin] : []),
      versions,
    );
  }

  /** The day a change starts by default: the next version's, else today. */
  const startsOn = (c: BillRateCard) => c.next?.from ?? c.today;

  /**
   * Add line, or a line's pencil (brief 1E-24, mockup v84): the line's name, service, unit, bill
   * rate, when it applies, a minimum, and a cancellation's notice rows. Saves into the version that
   * starts that day, else a new version.
   */
  function lineModal(c: BillRateCard, l?: Line) {
    const name = field("Line name", l?.ownName ?? "", {
      placeholder: l ? l.name : "As it prints on the invoice",
    });
    const service = select(
      "Service",
      [
        ["", "Pick a service"],
        ...c.choices.services.map((s) => [s.id, s.name] as const),
        ["mileage", "Mileage"],
      ],
      l ? (l.serviceId ?? "mileage") : "",
    );
    const unit = select("Unit", [], null);
    const unitsFor = (serviceId: string) =>
      serviceId === "mileage"
        ? c.choices.units.filter((u) => u.countedBy === "distance")
        : c.choices.units.filter((u) =>
            (c.choices.services.find((s) => s.id === serviceId)?.unitIds ?? []).includes(u.id),
          );
    const fillUnits = () => {
      const list = l
        ? c.choices.units.filter((u) => u.id === l.unitId)
        : unitsFor(service.input.value);
      unit.input.replaceChildren(
        ...(list.length || !l ? list : []).map((u) =>
          el("option", { value: u.id, selected: u.id === l?.unitId }, u.label),
        ),
        ...(l && !list.length ? [el("option", { value: l.unitId, selected: true }, l.unit)] : []),
      );
    };
    service.input.disabled = !!l;
    unit.input.disabled = !!l;
    const rate = field("Bill rate ($)", l && l.rate ? String(l.rate) : "", {
      inputmode: "decimal",
      placeholder: "33.00",
    });
    const applies = select(
      "Applies",
      [
        ["always", "Always"],
        ["up_to", "Up to a number of hours"],
        ["past", "Past a number of hours"],
        ...c.choices.periods.map((p) => [`period:${p.id}`, p.name] as const),
      ],
      l ? (l.applies === "period" ? `period:${l.appliesPeriodId}` : l.applies) : "always",
    );
    const hours = field("Hours", l?.appliesHours ? String(l.appliesHours) : "", {
      inputmode: "decimal",
      placeholder: "2",
    });
    const showHours = () => {
      const on = applies.input.value === "up_to" || applies.input.value === "past";
      hours.el.hidden = !on;
      hours.el.style.display = on ? "" : "none";
    };
    applies.input.addEventListener("change", showHours);
    const minimum = field("Minimum ($)", l?.minimumCharge ? l.minimumCharge.toFixed(2) : "", {
      inputmode: "decimal",
      placeholder: "e.g. 35.00",
    });
    const rule = field("Rule", l?.rule ?? "", { placeholder: "e.g. First 30 minutes free" });
    const from = field("Starts", startsOn(c), { type: "date" });
    // A cancellation's notice rows: the line's own, else the unit's.
    const notice = el("div", { class: "field wide" });
    let rows: { under: HTMLInputElement; charge: HTMLInputElement; how: HTMLSelectElement }[] = [];
    const drawNotice = () => {
      const u = c.choices.units.find((x) => x.id === unit.input.value);
      rows = [];
      if (u?.countedBy !== "cancellation") {
        notice.replaceChildren();
        notice.style.display = "none";
        return;
      }
      notice.style.display = "";
      const body = el("tbody");
      const add = (t?: NoticeRow) => {
        const under = el("input", {
          type: "text",
          inputmode: "numeric",
          value: t ? String(t.under) : "",
          "aria-label": "Notice under (hours)",
          style: "width:80px",
        });
        const charge = el("input", {
          type: "text",
          inputmode: "decimal",
          value: t ? String(t.percent ?? t.amount ?? "") : "",
          "aria-label": "Charge",
          style: "width:90px",
        });
        const how = el(
          "select",
          { "aria-label": "Charged as" },
          el(
            "option",
            { value: "percent", selected: !t || t.percent !== null },
            `% of the ${u.of}`,
          ),
          el("option", { value: "amount", selected: !!t && t.percent === null }, "Fixed ($)"),
        );
        rows.push({ under, charge, how });
        body.append(
          el("tr", {}, el("td", {}, "Under ", under, " hours"), el("td", {}, charge, " ", how)),
        );
      };
      const start = l?.noticeRows.length ? l.noticeRows : u.rows;
      if (start.length) start.forEach((t) => add(t));
      else add();
      const more = el("button", { type: "button", class: "btn sm" }, icon("plus"), " Add a row");
      more.addEventListener("click", () => add());
      notice.replaceChildren(
        el("label", {}, "By notice"),
        el(
          "div",
          { class: "tw" },
          el(
            "table",
            {},
            el("thead", {}, el("tr", {}, el("th", {}, "Notice"), el("th", {}, "Charge"))),
            body,
          ),
        ),
        more,
      );
    };
    service.input.addEventListener("change", () => {
      fillUnits();
      drawNotice();
    });
    unit.input.addEventListener("change", drawNotice);
    fillUnits();
    showHours();
    drawNotice();
    const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
    const m = openModal({
      title: l ? `Change ${l.name}` : "Add a line",
      sub: c.next
        ? `Saves into version ${c.next.number}, which starts ${c.next.fromWords}.`
        : "Saves as a new version of the bill rate card.",
      body: [
        el(
          "div",
          { class: "form formgrid", style: "max-width:none" },
          name.el,
          service.el,
          unit.el,
          rate.el,
          applies.el,
          hours.el,
          minimum.el,
          rule.el,
          from.el,
          notice,
        ),
        el(
          "p",
          { class: "small muted", style: "margin:0" },
          "A service can have more than one line. The line that applies is used; a minimum lifts a smaller charge to it.",
        ),
        ...(c.claims
          ? [
              el(
                "p",
                { class: "small muted", style: "margin:0" },
                "Claim codes for this client are set in its CMS-1500 setup.",
              ),
            ]
          : []),
      ],
      actions: [save],
    });
    save.addEventListener("click", async () => {
      save.disabled = true;
      const a = applies.input.value;
      const res = await api<{ version: { id: string; number: number; from: string } }>(
        "POST",
        `/api/bill-rate-cards/${encodeURIComponent(c.id)}/lines`,
        {
          lineId: l?.id,
          serviceId: service.input.value === "mileage" ? null : service.input.value,
          unitId: unit.input.value,
          name: name.input.value,
          rate: rate.input.value,
          applies: a.startsWith("period:") ? "period" : a,
          appliesHours: hours.input.value || null,
          appliesPeriodId: a.startsWith("period:") ? a.slice(7) : null,
          minimumCharge: minimum.input.value || null,
          // A line from before keeps its minimum units.
          minimumUnits: l?.minimumUnits ?? null,
          rule: rule.input.value,
          noticeRows: rows
            .filter((x) => x.under.value.trim() || x.charge.value.trim())
            .map((x) => ({
              under: x.under.value,
              percent: x.how.value === "percent" ? x.charge.value : null,
              amount: x.how.value === "amount" ? x.charge.value : null,
            })),
          from: from.input.value,
        },
      );
      save.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      picked = res.body.version.id;
      toast(
        `Saved into version ${res.body.version.number}`,
        `Starts ${usDay(res.body.version.from)}`,
      );
      await load();
    });
  }

  /** Add a differential: saves as a new version from a date. */
  function diffModal(c: BillRateCard, d?: Diff) {
    const name = field("Name", d?.name ?? "", { placeholder: "e.g. Weekend" });
    const when = select(
      "When",
      [...c.choices.periods.map((p) => [`period:${p.id}`, p.name] as const), ...WHEN_OTHER],
      d
        ? d.whenKind === "period"
          ? `period:${d.periodId}`
          : d.whenKind
        : c.choices.periods[0]
          ? `period:${c.choices.periods[0].id}`
          : "week_hours",
    );
    const hours = field("Hours", d?.hours ? String(d.hours) : "", {
      inputmode: "decimal",
      placeholder: "40",
    });
    const kind = select(
      "Kind",
      [
        ["rate", "Replaces the bill rate"],
        ["surcharge", "Added on top"],
      ],
      d?.kind ?? "rate",
    );
    const how = select(
      "Bill rate",
      c.choices.how.map((h) => [h.value, h.label] as const),
      d?.how ?? "times",
    );
    const amount = field("Amount", d ? String(d.amount) : "", { placeholder: "1.5 or 3.00" });
    const services = pills(
      "Services",
      "checkbox",
      c.choices.cardServices.map((s) => [s.id, s.name] as const),
      d?.serviceIds ?? [],
      "None ticked: all on this card.",
    );
    const from = field("Starts", startsOn(c), { type: "date" });
    const showHours = () => {
      const on = ["week_hours", "day_hours", "short_notice"].includes(when.input.value);
      hours.el.hidden = !on;
      hours.el.style.display = on ? "" : "none";
    };
    when.input.addEventListener("change", showHours);
    showHours();
    const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
    const m = openModal({
      title: d ? `Change ${d.name}` : "Add a differential",
      sub: c.next
        ? `Saves into version ${c.next.number}, which starts ${c.next.fromWords}.`
        : "Saves as a new version of the bill rate card.",
      body: [
        el(
          "div",
          { class: "form" },
          name.el,
          el("div", { class: "formgrid" }, when.el, hours.el, kind.el, how.el, amount.el, from.el),
          services.el,
        ),
      ],
      actions: [save],
    });
    save.addEventListener("click", async () => {
      save.disabled = true;
      const w = when.input.value;
      const res = await api<{ version: { id: string; number: number } }>(
        "POST",
        `/api/bill-rate-cards/${encodeURIComponent(c.id)}/differentials`,
        {
          differentialId: d?.id,
          name: name.input.value,
          kind: kind.input.value,
          whenKind: w.startsWith("period:") ? "period" : w,
          periodId: w.startsWith("period:") ? w.slice(7) : null,
          hours: hours.input.value || null,
          how: how.input.value,
          amount: amount.input.value,
          serviceIds: services.values(),
          from: from.input.value,
        },
      );
      save.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      picked = res.body.version.id;
      toast(`Saved into version ${res.body.version.number}`, name.input.value);
      await load();
    });
  }

  await load();
}

interface NewCard {
  card: string | null;
  client: string;
  contract: string;
  from: string;
  started: string | null;
  lines: (Line & { business: string })[];
}

/**
 * The contract's Bill rate card button (brief 1E-16): opens the card, or makes it from the standard
 * prices, keeping only the lines the contract covers.
 */
export async function openBillRateCard(contractId: string, go: (path: string) => void) {
  const res = await api<NewCard>(
    "GET",
    `/api/bill-rate-cards/new?contractId=${encodeURIComponent(contractId)}`,
  );
  if (!res.ok) return toast("The bill rate card couldn't be opened", res.body.error);
  const n = res.body;
  if (n.card) return go(`/homecare/rates/${n.card}`);
  const keep = pills(
    "Lines the contract covers",
    "checkbox",
    n.lines.map((l) => [l.id, `${l.service} · ${l.unit} · ${l.rateWords}`] as const),
    n.lines.map((l) => l.id),
  );
  const from = field("Starts", n.from, { type: "date" });
  const make = el(
    "button",
    { type: "button", class: "btn pri" },
    icon("check"),
    " Make the bill rate card",
  );
  const m = openModal({
    title: `Bill rate card · ${n.client}`,
    sub: n.started ?? "No standard prices yet: the card starts empty.",
    body: [el("div", { class: "form" }, ...(n.lines.length ? [keep.el] : []), from.el)],
    actions: [make],
  });
  make.addEventListener("click", async () => {
    make.disabled = true;
    const r = await api<{ id: string }>("POST", "/api/bill-rate-cards", {
      contractId,
      keep: keep.values(),
      from: from.input.value,
    });
    make.disabled = false;
    if (!r.ok) return refuse(m.note, r.body.error);
    m.close();
    toast("Bill rate card made", n.contract);
    go(`/homecare/rates/${r.body.id}`);
  });
}
